import { assert } from "@tento-core/assert";
import { DomUtils } from "@tento-ui/dom";
import { LayoutDebugOverlayPlan } from "./dbg-layout-overlay-plan.ts";
import type { RowLocalId } from "@tento-lona/sheets";
import { SheetDebugOverlay } from "@tento-ui/components/sheet/dbg-sheet-overlay";
import { SheetDelegate } from "../sheet-delegate.ts";

export class LayoutDebugOverlayRenderer {
  static readonly #ASSERTION_PREFIX =
    "Expected SheetDebugOverlay for debug overlay";

  #live = new Map<
    string,
    {
      target: LayoutDebugOverlayPlan.Spec["target"];
      rowId: RowLocalId;
    }
  >();

  readonly #staticChild: SheetDelegate.StaticChildHandle;
  readonly #fixedChild: SheetDelegate.FixedChildHandle;
  constructor(
    fixedChild: SheetDelegate.FixedChildHandle,
    staticChild: SheetDelegate.StaticChildHandle,
  ) {
    this.#fixedChild = fixedChild;
    this.#staticChild = staticChild;
  }

  apply(plan: LayoutDebugOverlayPlan.Plan): void {
    for (const op of plan.ops) {
      switch (op.type) {
        case "dismiss":
          this.#dismiss(op);
          break;
        case "reveal":
          this.#reveal(op.spec);
          break;
        case "update":
          this.#update(op.spec);
          break;
      }
    }
  }

  clear(specs: Map<string, LayoutDebugOverlayPlan.Spec>): void {
    for (const [, spec] of specs) {
      this.#dismiss({
        type: "dismiss",
        id: spec.id,
        target: spec.target,
        rowId: spec.rowId,
      });
    }

    // Best-effort sweep for overlays that may have been pending append and
    // never made it into `specs`/DOM query results at dismissal time.
    for (const [id, entry] of this.#live) {
      this.#dismiss({
        type: "dismiss",
        id,
        target: entry.target,
        rowId: entry.rowId,
      });
    }
  }

  #reveal(spec: LayoutDebugOverlayPlan.Spec): void {
    if (spec.target === "static") {
      this.#revealStatic(spec);
      return;
    }

    this.#staticChild.dismiss(spec.id, spec.rowId);

    const $element = this.#makeOverlay(spec);
    this.#markLive(spec.id, "fixed", spec.rowId);
    this.#fixedChild.reveal(
      spec.id,
      $element,
      {
        x: spec.anchor,
        y: spec.rowId,
      },
      {
        animate: false,
        inset: {
          left: spec.inset.left,
          top: spec.inset.top,
        },
        widthBehavior: Math.max(1, spec.widthPx),
      },
    );
  }

  #revealStatic(spec: LayoutDebugOverlayPlan.Spec): void {
    this.#fixedChild.dismiss(spec.id, "instant");
    const $element = this.#makeOverlay(spec);
    this.#markLive(spec.id, "static", spec.rowId);
    this.#staticChild.reveal(spec.id, $element, spec.rowId, {
      fillWidth: true,
    });
  }

  #update(spec: LayoutDebugOverlayPlan.Spec): void {
    if (spec.target === "static") {
      this.#updateStatic(spec);
      return;
    }

    const $element = this.#fixedChild.getFixedChild(spec.id);
    if (!$element) {
      this.#reveal(spec);
      return;
    }
    this.#markLive(spec.id, "fixed", spec.rowId);
    const $overlay = this.#assertSheetDebugOverlay(
      $element,
      "update-fixed",
      spec.id,
    );
    $overlay.bind({
      label: spec.label,
      color: spec.color,
      widthPx: spec.widthPx,
      heightPx: spec.heightPx,
    });

    const repositioned = this.#fixedChild.repositionFixedChild(
      spec.id,
      spec.anchor,
      spec.inset.left,
    );
    const insetUpdated = this.#fixedChild.updateFixedChildInset(
      spec.id,
      {
        left: spec.inset.left,
        top: spec.inset.top,
      },
      spec.rowId,
    );

    if (!repositioned || !insetUpdated) {
      DomUtils.removeFromDom($element, false);
      this.#reveal(spec);
    }
  }

  #updateStatic(spec: LayoutDebugOverlayPlan.Spec): void {
    this.#fixedChild.dismiss(spec.id, "instant");
    const $element = this.#staticChild.get(spec.id, spec.rowId);
    if (!$element) {
      this.#revealStatic(spec);
      return;
    }
    this.#markLive(spec.id, "static", spec.rowId);
    const $overlay = this.#assertSheetDebugOverlay(
      $element,
      "update-static",
      spec.id,
    );
    $overlay.bind({
      label: spec.label,
      color: spec.color,
      widthPx: spec.widthPx,
      heightPx: spec.heightPx,
    });
  }

  #makeOverlay(spec: LayoutDebugOverlayPlan.Spec): SheetDebugOverlay {
    return SheetDebugOverlay.make().bind({
      label: spec.label,
      color: spec.color,
      widthPx: spec.widthPx,
      heightPx: spec.heightPx,
    });
  }

  #dismiss(
    op: Extract<LayoutDebugOverlayPlan.Op, { type: "dismiss" }>,
  ): void {
    if (op.target === "static") {
      const $element = this.#staticChild.get(op.id, op.rowId);
      this.#staticChild.dismiss(op.id, op.rowId);
      if ($element) {
        this.#assertSheetDebugOverlay($element, "dismiss-static", op.id);
      }
      this.#live.delete(op.id);
      return;
    }

    const $element = this.#fixedChild.getFixedChild(op.id);
    this.#fixedChild.dismiss(op.id, "instant");
    if ($element) {
      this.#assertSheetDebugOverlay($element, "dismiss-fixed", op.id);
    }
    this.#live.delete(op.id);
  }

  #assertSheetDebugOverlay(
    $element: unknown,
    phase: string,
    id: string,
  ): SheetDebugOverlay {
    assert(
      $element instanceof SheetDebugOverlay,
      `${LayoutDebugOverlayRenderer.#ASSERTION_PREFIX}; phase=${phase}; id=${id}`,
    );
    return $element as SheetDebugOverlay;
  }

  #markLive(
    id: string,
    target: LayoutDebugOverlayPlan.Spec["target"],
    rowId: RowLocalId,
  ): void {
    this.#live.set(id, {
      target,
      rowId,
    });
  }
}
